import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Row, Col } from 'antd';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormInput from 'components-desktop/FormInput';
import FormSelect from 'components-desktop/FormSelect';
import FormSelect2 from 'components-desktop/FormSelect2';
import FormTextArea from 'components-desktop/FormTextArea';
import ChangeHistoriesButton from 'components-desktop/histories/ChangeHistoriesButton';
import helpers from 'misc/helpers';

const propTypes = {
  patient: PropTypes.object,
  form: PropTypes.object.isRequired,
};

@autobind
class PatientDetails extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      caregivers: [],
      coordinators: [],
      teams: [],

      mutual: null,
      isSaveButtonEnable: true,
    };
  }

  componentDidMount() {
    this.id = this.parseQueryParam('id', this.props.location);
    this.getOtherDetails();
  }

  async getOtherDetails() {
    const promise1 = this.callServiceForObjects('/caregiver/get_id_names');
    const promise2 = this.callServiceForObjects('/coordinator/get_id_names');
    const promise3 = this.callServiceForObjects('/team/get_id_names');
    const promise4 = this.callServiceForRemoteObject('/patient/find_mutual', { patientId: this.id });

    const results = await Promise.all([promise1, promise2, promise3, promise4]);

    this.setState({
      caregivers: results[0],
      coordinators: results[1],
      teams: results[2],
      mutual: results[3],
    });
  }

  async savePatient(values) {
    try {
      this.setState({
        isSaveButtonEnable: false,
      });

      const params = {
        id: this.id,
        firstName: helpers.extraFormInputValue(values, 'firstName'),
        middleName: helpers.extraFormInputValue(values, 'middleName'),
        lastName: helpers.extraFormInputValue(values, 'lastName'),
        gender: helpers.extraFormSelectValue(values, 'gender'),
        birth: helpers.extraFormDateValue(values, 'birth'),
        ssn: helpers.extraFormInputValue(values, 'ssn'),
        language: helpers.extraFormInputValue(values, 'language'),
        contractPackage: helpers.extraFormSelectValue(values, 'contractPackage'),
        street: helpers.extraFormInputValue(values, 'street'),
        city: helpers.extraFormInputValue(values, 'city'),
        // state: helpers.extraFormInputValue(values, 'state'),
        zipCode: helpers.extraFormInputValue(values, 'zipCode'),
        homePhone: helpers.extraFormInputValue(values, 'homePhone'),
        cellPhone: helpers.extraFormInputValue(values, 'cellPhone'),
        coordinatorId: helpers.extraFormSelectValue(values, 'coordinatorId'),
        teamId: helpers.extraFormSelectValue(values, 'teamId'),
        caseManagerName: helpers.extraFormInputValue(values, 'caseManagerName'),
        caseManagerPhone: helpers.extraFormInputValue(values, 'caseManagerPhone'),
        remark: helpers.extraFormTextAreaValue(values, 'remark'),
        medicaidNo: helpers.extraFormInputValue(values, 'medicaidNo'),
        medicareNo: helpers.extraFormInputValue(values, 'medicareNo'),
        privateInsurancePolicy: helpers.extraFormInputValue(values, 'privateInsurancePolicy'),
        emergContName: helpers.extraFormInputValue(values, 'emergContName'),
        emergContRelation: helpers.extraFormInputValue(values, 'emergContRelation'),
        emergContNumber: helpers.extraFormInputValue(values, 'emergContNumber'),
        physicianName: helpers.extraFormInputValue(values, 'physicianName'),
        physicianPhone: helpers.extraFormInputValue(values, 'physicianPhone'),
        physicianFax: helpers.extraFormInputValue(values, 'physicianFax'),
        physicianAddr: helpers.extraFormInputValue(values, 'physicianAddr'),
        guardianName: helpers.extraFormInputValue(values, 'guardianName'),
        diagnosis: helpers.extraFormTextAreaValue(values, 'diagnosis'),
        medications: helpers.extraFormTextAreaValue(values, 'medications'),
        diagnosisCode1: helpers.extraFormInputValue(values, 'diagnosisCode1'),
        diagnosisCode2: helpers.extraFormInputValue(values, 'diagnosisCode2'),
        diagnosisCode3: helpers.extraFormInputValue(values, 'diagnosisCode3'),
        diagnosisCode4: helpers.extraFormInputValue(values, 'diagnosisCode4'),
        caregiverId: helpers.extraFormSelectValue(values, 'caregiverId'),
        caregiverRelation: helpers.extraFormInputValue(values, 'caregiverRelation'),
        backupCaregiver1: helpers.extraFormInputValue(values, 'backupCaregiver1'),
        backupCaregiver2: helpers.extraFormInputValue(values, 'backupCaregiver2'),
        // noteFromPatient: helpers.extraFormTextAreaValue(values, 'noteFromPatient'),
        noteFromStaff: helpers.extraFormTextAreaValue(values, 'noteFromStaff'),
        applyStatus: helpers.extraFormSelectValue(values, 'applyStatus'),
        // applyDate: helpers.extraFormDateValue(values, 'applyDate'),
      };
      await this.callService('/patient/update', params);

      this.notifySuccess('Success', 'Patient profile has been updated.');
    } finally {
      this.setState({
        isSaveButtonEnable: true,
      });
    }
  }

  onResetClick() {
    window.location.reload();
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.savePatient(values);
      }
    });
  }

  validateAtLeastOnePhone(rule, value, callback, source, options) {
    const { form } = this.props;

    if (!value) {
      const otherValue = form.getFieldValue(rule.field === 'homePhone' ? 'cellPhone' : 'homePhone');

      if (!otherValue) {
        callback('At least one phone is required.');
      } else {
        callback();
      }
    } else {
      callback();
    }
  }

  validateCity(rule, value, callback, source, options) {
    if (value === undefined || value === null || value === '') {
      callback('This field should not be empty.');
    } else {
      const pattern = /^[A-Za-z][A-Za-z ]*[A-Za-z]$/g;
      if (pattern.test(value)) {
        callback();
      } else {
        callback('City name is not valid.');
      }
    }
  }

  validateMedicaidNo(rule, value, callback, source, options) {
    if (value === undefined || value === null || value === '') {
      callback('This field should not be empty.');
    } else {
      const pattern = /^[A-Z]{2}[0-9]{5}[A-Z]$/g;
      if (pattern.test(value)) {
        callback();
      } else {
        callback('Invalid Medicaid number format, correct format: AB12345C');
      }
    }
  }

  isInRenderableState() {
    return !!this.props.patient;
  }

  renderContent() {
    const { patient, form, ...layoutProps } = this.props;
    const { caregivers, coordinators, teams, mutual, isSaveButtonEnable } = this.state;

    return (
      <FlexColContainer
        {...layoutProps}
        alignItems="center"
      >
        <styledComp.AntdForm
          width="match_parent"
          height="wrap_content"
          onSubmit={this.handleSubmit}
        >
          {/* Content */}
          <FlexColContainer
            width="match_parent"
            height="wrap_content"
            padding="20px 20px"
          >
            {mutual &&
              <FlexRowContainer
                width="match_parent"
                height="wrap_content"
                margin="0 0 16px 0"
                padding="16px 16px"
                backgroundColor="#d9edf7"
                border="1 solid #bce8f1"
                boxShadow="0 1px 3px rgba(0,0,0,.1), 0 1px 2px rgba(0,0,0,.18)"
              >
                <styledComp.Span
                  width="wrap_content"
                  height="wrap_content"
                  margin="0 4px 0 0"
                  color="#31708f"
                  fontSize="14px"
                >
                  Mutual Case:
                </styledComp.Span>

                <styledComp.A
                  width="wrap_content"
                  height="wrap_content"
                  color="#31708f"
                  href={`/patients/patient_profile?id=${mutual.getId()}`}
                  target="_blank"
                >
                  {mutual.getString('fullName')}
                </styledComp.A>
              </FlexRowContainer>
            }

            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              margin="0 0 20px 0"
            >
              <FlexRowContainer
                width="match_parent"
                height="wrap_content"
                margin="0 0 28px 0"
                justifyContent="space-between"
                alignItems="center"
              >
                <styledComp.Span
                  width="wrap_content"
                  height="wrap_content"
                  color="textPrimaryDark"
                  fontSize="24px"
                  fontWeight="300"
                >
                  Personal Info
                </styledComp.Span>

                <ChangeHistoriesButton
                  width="wrap_content"
                  height="wrap_content"
                  title="Change History"
                  table="Patient"
                  rowId={Number(this.id)}
                />
              </FlexRowContainer>

              <Row type="flex" gutter={24}>
                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="firstName"
                    label="First Name"
                    placeholder=""
                    initialValue={patient.getString('firstName')}
                    isRequired
                  />
                </Col>

                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="middleName"
                    label="Middle Name"
                    placeholder=""
                    initialValue={patient.getString('middleName')}
                  />
                </Col>

                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="lastName"
                    label="Last Name"
                    placeholder=""
                    initialValue={patient.getString('lastName')}
                    isRequired
                  />
                </Col>

                <Col span={6}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="gender"
                    label="Gender"
                    placeholder="Select..."
                    allowClear={false}
                    initialValue={patient.getString('gender')}
                    options={['Female', 'Male']}
                    isRequired
                  />
                </Col>

                <Col span={6}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="birth"
                    label="Date of Birth"
                    placeholder="mm/dd/yyyy"
                    extra="Select date from calendar"
                    initialValue={patient.getMoment('birth')}
                    isRequired
                  />
                </Col>

                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="ssn"
                    label="Social Security Number (SSN)"
                    placeholder="111-11-1111"
                    inputMask="999-99-9999"
                    initialValue={patient.getString('ssn')}
                  />
                </Col>

                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="language"
                    label="Language"
                    placeholder="English"
                    extra="Patient's preferred language"
                    initialValue={patient.getString('language')}
                    isRequired
                  />
                </Col>

                <Col span={6}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="contractPackage"
                    label="Contract Package"
                    placeholder="Select..."
                    allowClear={false}
                    initialValue={patient.getBoolean('contractPackage')?.toString()}
                    options={[{ value: 'false', text: 'No' }, { value: 'true', text: 'Yes' }]}
                    isRequired
                  />
                </Col>
              </Row>
            </FlexColContainer>

            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              margin="0 0 20px 0"
            >
              <styledComp.Span
                width="wrap_content"
                height="wrap_content"
                margin="0 0 28px 0"
                color="textPrimaryDark"
                fontSize="24px"
                fontWeight="300"
              >
                Contact Info
              </styledComp.Span>

              <Row type="flex" gutter={24}>
                <Col span={12}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="street"
                    label="Street address and Suite or Apt. #"
                    placeholder="123 Main Street, Apt. 103"
                    extra="We currently only accept applications from New York."
                    initialValue={patient.getString('street')}
                    isRequired
                  />
                </Col>

                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="city"
                    label="City"
                    placeholder=""
                    initialValue={patient.getString('city')}
                    validator={this.validateCity}
                  />
                </Col>

                <Col span={2}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="state"
                    label="State"
                    placeholder=""
                    disabled
                    initialValue="NY"
                    isRequired
                  />
                </Col>

                <Col span={4}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="zipCode"
                    label="ZIP Code"
                    placeholder=""
                    initialValue={patient.getString('zipCode')}
                    isRequired
                  />
                </Col>
              </Row>

              <Row type="flex" gutter={24}>
                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="homePhone"
                    label="Home Phone"
                    placeholder="(111) 111-1111"
                    inputMask="(999) 999-9999"
                    initialValue={patient.getString('homePhone')}
                    validator={this.validateAtLeastOnePhone}
                  />
                </Col>

                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="cellPhone"
                    label="Cell Phone"
                    placeholder="(111) 111-1111"
                    inputMask="(999) 999-9999"
                    initialValue={patient.getString('cellPhone')}
                    validator={this.validateAtLeastOnePhone}
                  />
                </Col>
              </Row>
            </FlexColContainer>

            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              margin="0 0 20px 0"
            >
              <styledComp.Span
                width="wrap_content"
                height="wrap_content"
                margin="0 0 28px 0"
                color="textPrimaryDark"
                fontSize="24px"
                fontWeight="300"
              >
                General Info
              </styledComp.Span>

              <Row type="flex" gutter={24}>
                <Col span={6}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="coordinatorId"
                    label="Coordinator"
                    placeholder="Select..."
                    options={coordinators}
                    initialValue={patient.getLong('coordinatorId')}
                  />
                </Col>

                <Col span={6}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="teamId"
                    label="Team"
                    placeholder="Select..."
                    options={teams}
                    initialValue={patient.getLong('teamId')}
                  />
                </Col>

                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="caseManagerName"
                    label="Case Manager Name"
                    placeholder=""
                    initialValue={patient.getString('caseManagerName')}
                  />
                </Col>

                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="caseManagerPhone"
                    label="Case Manager Phone"
                    placeholder="(111) 111-1111"
                    inputMask="(999) 999-9999"
                    initialValue={patient.getString('caseManagerPhone')}
                  />
                </Col>

                <Col span={24}>
                  <FormTextArea
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="remark"
                    label="Remark"
                    rows={5}
                    max={2000}
                    extra="For internal use only, will not displayed to the patient."
                    initialValue={patient.getString('remark')}
                  />
                </Col>
              </Row>
            </FlexColContainer>

            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              margin="0 0 20px 0"
            >
              <styledComp.Span
                width="wrap_content"
                height="wrap_content"
                margin="0 0 28px 0"
                color="textPrimaryDark"
                fontSize="24px"
                fontWeight="300"
              >
                Patient Details
              </styledComp.Span>

              <Row type="flex" gutter={24}>
                <Col span={8}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="medicaidNo"
                    label="Medicaid No."
                    placeholder=""
                    initialValue={patient.getString('medicaidNo')}
                    // isRequired
                    validator={this.validateMedicaidNo}
                  />
                </Col>

                <Col span={8}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="medicareNo"
                    label="Medicare No."
                    placeholder=""
                    initialValue={patient.getString('medicareNo')}
                  />
                </Col>

                <Col span={8}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="privateInsurancePolicy"
                    label="Private Insurance Policy"
                    placeholder=""
                    initialValue={patient.getString('privateInsurancePolicy')}
                  />
                </Col>

                <Col span={8}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="emergContName"
                    label="Emergency Contact Person"
                    placeholder=""
                    initialValue={patient.getString('emergContName')}
                  />
                </Col>

                <Col span={8}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="emergContRelation"
                    label="Emergency Contact Relationship"
                    placeholder=""
                    initialValue={patient.getString('emergContRelation')}
                  />
                </Col>

                <Col span={8}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="emergContNumber"
                    label="Emergency Contact Phone Number"
                    placeholder=""
                    initialValue={patient.getString('emergContNumber')}
                  />
                </Col>

                <Col span={8}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="physicianName"
                    label="Physician Name"
                    placeholder=""
                    initialValue={patient.getString('physicianName')}
                  />
                </Col>

                <Col span={8}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="physicianPhone"
                    label="Physician Phone"
                    placeholder=""
                    initialValue={patient.getString('physicianPhone')}
                  />
                </Col>

                <Col span={8}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="physicianFax"
                    label="Physician Fax"
                    placeholder=""
                    initialValue={patient.getString('physicianFax')}
                  />
                </Col>

                <Col span={16}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="physicianAddr"
                    label="Physician Address"
                    placeholder=""
                    initialValue={patient.getString('physicianAddr')}
                  />
                </Col>

                <Col span={8}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="guardianName"
                    label="Guardian Name"
                    placeholder=""
                    initialValue={patient.getString('guardianName')}
                  />
                </Col>

                <Col span={this.isMeRealAgency() ? 12 : 24}>
                  <FormTextArea
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="medications"
                    label="Medications"
                    rows={3}
                    max={2000}
                    initialValue={patient.getString('medications')}
                  />
                </Col>

                {this.isMeRealAgency() &&
                  <>
                    <Col span={12}>
                      <FormTextArea
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="diagnosis"
                        label="Diagnosis"
                        rows={3}
                        max={4000}
                        initialValue={patient.getString('diagnosis')}
                      />
                    </Col>

                    <Col span={6}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="diagnosisCode1"
                        label="Diagnosis Code 1"
                        placeholder=""
                        initialValue={patient.getString('diagnosisCode1')}
                      />
                    </Col>

                    <Col span={6}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="diagnosisCode2"
                        label="Diagnosis Code 2"
                        placeholder=""
                        initialValue={patient.getString('diagnosisCode2')}
                      />
                    </Col>

                    <Col span={6}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="diagnosisCode3"
                        label="Diagnosis Code 3"
                        placeholder=""
                        initialValue={patient.getString('diagnosisCode3')}
                      />
                    </Col>

                    <Col span={6}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="diagnosisCode4"
                        label="Diagnosis Code 4"
                        placeholder=""
                        initialValue={patient.getString('diagnosisCode4')}
                      />
                    </Col>
                  </>
                }
              </Row>
            </FlexColContainer>

            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              margin="0 0 20px 0"
            >
              <styledComp.Span
                width="wrap_content"
                height="wrap_content"
                margin="0 0 28px 0"
                color="textPrimaryDark"
                fontSize="24px"
                fontWeight="300"
              >
                Patient Application
              </styledComp.Span>

              <Row type="flex" gutter={24}>
                <Col span={6}>
                  <FormSelect2
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="caregiverId"
                    label="Caregiver Name"
                    placeholder="Select..."
                    options={caregivers}
                    showIdAfterName
                    initialValue={patient.getLong('caregiverId')}
                  />
                </Col>

                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="caregiverRelation"
                    label="Family relation with this caregiver"
                    placeholder="e.g. daughter in law"
                    initialValue={patient.getString('caregiverRelation')}
                  />
                </Col>

                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="backupCaregiver1"
                    label="Backup Caregiver 1"
                    placeholder=""
                    initialValue={patient.getString('backupCaregiver1')}
                  />
                </Col>

                <Col span={6}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="backupCaregiver2"
                    label="Backup Caregiver 2"
                    placeholder=""
                    initialValue={patient.getString('backupCaregiver2')}
                  />
                </Col>

                <Col span={12}>
                  <FormTextArea
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="noteFromPatient"
                    label="Additional Message from Patient"
                    rows={3}
                    max={2000}
                    disabled
                    initialValue={patient.getString('noteFromPatient')}
                  />
                </Col>

                <Col span={12}>
                  <FormTextArea
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="noteFromStaff"
                    label="Additional Message to Patient"
                    rows={3}
                    max={2000}
                    initialValue={patient.getString('noteFromStaff')}
                  />
                </Col>

                <Col span={12}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="applyStatus"
                    label="Apply Status"
                    placeholder="Select..."
                    extra="The status of this patient application."
                    allowClear={false}
                    options={['PROCESSING', 'APPROVED', 'REJECTED', 'CANCELED', 'ON_HOLD', 'DISCHARGED']}
                    initialValue={patient.getString('applyStatus')}
                    isRequired
                  />
                </Col>

                <Col span={12}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="applyDate"
                    label="Apply Date"
                    placeholder=""
                    disabled
                    initialValue={patient.getString('applyDateUsString')}
                    isRequired
                  />
                </Col>
              </Row>
            </FlexColContainer>
          </FlexColContainer>

          {/* Footer */}
          <FlexRowContainer
            width="match_parent"
            height="wrap_content"
            padding="20px 20px"
            justifyContent="center"
            backgroundColor="containerGrayBackground"
          >
            <CustomButton
              width="wrap_content"
              height="32px"
              margin="0 4px 0 0"
              backgroundColor="blue_steel"
              fontSize="14px"
              type="primary"
              loading={!isSaveButtonEnable}
              htmlType="submit"
            >
              SAVE
            </CustomButton>

            <styledComp.AntdButton
              width="wrap_content"
              height="32px"
              fontSize="14px"
              type="default"
              onClick={this.onResetClick}
            >
              RESET
            </styledComp.AntdButton>
          </FlexRowContainer>
        </styledComp.AntdForm>
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
PatientDetails.propTypes = propTypes;

export default Form.create()(PatientDetails);
